import type { SfJQuery } from '@app/types'
import type { ProgressChecker, ProgressUpdateData } from './types'

import $ from 'jquery'
import { SfLog } from '@app/adapters/logger'
import Sfetch from '@app/utils/sfetch'

const createProgressChecker = ({
  progressPathPayload,
}: {
  progressPathPayload: string
}): ProgressChecker => {
  let timer: ReturnType<typeof setTimeout>
  const progressPath = progressPathPayload
  let uninstalled = false

  const checkUpdate = (): void => {
    Sfetch.json<ProgressUpdateData>(progressPath, {}, { camelizeJSON: false })
      .then((data) => {
        /**
         * Guard against a race condition that can occur as follows:
         * 1. The user navigates away from the page when we haven't yet received a response from the server. Teardown is called on this progress checker.
         * 2. After the user navigates away, they receive a delayed response from the server and proceed into the then block of checkUpdate, that is poised to setup a new timeout
         * 3. We schedule a new timeout to poll the server, however the user is now at a page that doesn't necessarily need to be polled
         * */
        if (uninstalled) {
          return
        }

        let $html

        if (data?.status === 'ready') {
          window.eventHub.$emit('progress-checker:uninstall', progressPath)

          if (data.redirect) {
            window.Turbolinks.visit(data.redirect)
            return
          }

          if (data.html) {
            window.eventHub.$emit('progress-checker:replace-html', data.html)
          }
        } else {
          // in error or processing, only reschedule a polling if processing
          if (data?.status === 'processing') {
            registerTimeout()
          }

          if (data?.status_update) {
            window.eventHub.$emit(
              'progress-checker:add-html-to-container',
              data.status_update
            )
          }
        }

        if (data?.global_updates) {
          const keys = Object.keys(data.global_updates)

          keys.forEach((key) => {
            const val = data?.global_updates?.[key] as string
            $html = $(val) as SfJQuery
            $(key).replaceWith($html)
          })

          window.eventHub.$emit('progress-checker:global-replace-html')
        }
      })
      .catch((e) => {
        SfLog.warn('Error fetching progress update', {
          extra: {
            error: e,
          },
        })
      })
  }

  const getProgressPath = (): string => {
    return progressPath
  }

  const registerTimeout = (): void => {
    timer = setTimeout(checkUpdate, 2000)
  }

  registerTimeout()

  const teardown = (): void => {
    uninstalled = true

    if (timer) {
      clearTimeout(timer)
    }
  }

  return {
    getProgressPath,
    teardown,
    registerTimeout,
    checkUpdate,
  }
}

export default createProgressChecker
